import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { SELECT_STYLES, mergeSelectStyles } from "../../utils/selectStyles";
import { CButton, CCollapse, CFormInput, CFormSelect } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import * as Sentry from "@sentry/react";
import DashboardContent from "../../components/DashboardContent";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import RedirectHandler from "../../components/RedirectHandler";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../../assets/images/icons/copy.svg";
import {
  SCS_COMPARISON_TYPE_OPTIONS,
  SCS_GROUPING_TABLE_OPTIONS,
  SCS_TREATMENT_GROUP_ROLE_OPTIONS,
} from "../../utils/scsDescriptionOfStudiesOptions";
import {
  SCS_CLE_COMPARISON_TYPE_LABELS,
  SCS_CLE_COMPARISON_TYPE_VALUES,
  SCS_CLE_QUESTIONS,
  SCS_LAB_EVAL_TYPE_OPTIONS,
} from "../../utils/scsClinicalLaboratoryEvaluationsQuestions";
import { useScsTableOptions } from "../../hooks/useScsTableOptions";
import { emptyWhenUnanswered } from "../../utils/scsWizardPayload";

const isProduction = process.env.NODE_ENV === "production";

const FORM_ID = "scs-clinical-laboratory-evaluations-form";
const REMOVE_TOOLTIP_ID = "scs-cle-remove";
const DUPLICATE_TOOLTIP_ID = "scs-cle-duplicate";

// Repeatable blocks (the table/analysis sections and the comparison groups
// inside them) always keep at least one entry; the first entry is non-removable
// (mirrors the Analysis of Adverse Events / Description of Studies pattern).
const MIN_BLOCK_ENTRIES = 1;

const emptyTreatmentGroup = () => ({ name: "", role: "" });
const emptyComparisonPair = () => ({ treatment: "", comparator: "" });

// UI shape of one comparison grouping (Q3–Q6) inside a table/analysis section.
// Like the Analysis of Adverse Events comparison blocks it holds every branch
// answer, so switching the comparison type (Q4) never loses what was already
// typed; only the answers belonging to the selected type are persisted (see
// buildGroupPayload). Q4 is held as the on-screen wording and only becomes its
// saved value (single_arm / multi_arm / dose_finding) in the payload.
const emptyGroup = () => ({
  qLabAdditionalGroupingName: "",
  qLabAdditionalGroupingStudies: [],
  qLabComparisonType: "",
  qLabNonComparativeGroupNames: [""],
  qLabTreatmentGroups: [emptyTreatmentGroup()],
  qLabComparisonPairs: [emptyComparisonPair()],
  qLabDoseGroups: [""],
});

// UI shape of one table/analysis section (the whole Q1–Q6 flow). This page is
// organised around these sections only — Q1 blank ends the section, so nothing
// below it is shown or saved.
const emptyAnalysis = () => ({
  qLabTable: "",
  qLabEvalTypes: [],
  groupings: [emptyGroup()],
});

const seedList = list =>
  Array.isArray(list) && list.length > 0 ? [...list] : [""];

// Treatment group roles are saved lowercase ("treatment" / "comparator") but
// shown with the wording of SCS_TREATMENT_GROUP_ROLE_OPTIONS on screen — same
// convention as the Analysis of Adverse Events page.
const roleForPayload = role => (role || "").toLowerCase();
const roleForDisplay = role =>
  SCS_TREATMENT_GROUP_ROLE_OPTIONS.find(
    option => option.toLowerCase() === (role || "").toLowerCase()
  ) || "";

const LAB_EVAL_TYPE_LABELS = SCS_LAB_EVAL_TYPE_OPTIONS.map(
  option => option.label
);
const LAB_EVAL_TYPE_LABEL_SET = new Set(LAB_EVAL_TYPE_LABELS);
// Q2 answers are saved as the category labels; keep them in the page order so
// they never jump around as categories are picked.
const orderedEvalTypes = types => {
  const selected = new Set(
    (Array.isArray(types) ? types : []).filter(type =>
      LAB_EVAL_TYPE_LABEL_SET.has(type)
    )
  );
  return LAB_EVAL_TYPE_LABELS.filter(label => selected.has(label));
};

// Deep-clone a comparison grouping / table-analysis section already in its UI
// shape (the "Duplicate" controls), so the copy is fully independent from its
// source (mutating one never affects the other).
const cloneGroup = group => ({
  ...emptyGroup(),
  qLabAdditionalGroupingName: group?.qLabAdditionalGroupingName || "",
  qLabAdditionalGroupingStudies: Array.isArray(
    group?.qLabAdditionalGroupingStudies
  )
    ? [...group.qLabAdditionalGroupingStudies]
    : [],
  qLabComparisonType: group?.qLabComparisonType || "",
  qLabNonComparativeGroupNames: seedList(group?.qLabNonComparativeGroupNames),
  qLabTreatmentGroups: (group?.qLabTreatmentGroups?.length
    ? group.qLabTreatmentGroups
    : [emptyTreatmentGroup()]
  ).map(tg => ({ name: tg?.name || "", role: roleForDisplay(tg?.role) })),
  qLabComparisonPairs: (group?.qLabComparisonPairs?.length
    ? group.qLabComparisonPairs
    : [emptyComparisonPair()]
  ).map(pair => ({
    treatment: pair?.treatment || "",
    comparator: pair?.comparator || "",
  })),
  qLabDoseGroups: seedList(group?.qLabDoseGroups),
});

const cloneAnalysis = analysis => ({
  ...emptyAnalysis(),
  qLabTable: analysis?.qLabTable || "",
  qLabEvalTypes: orderedEvalTypes(analysis?.qLabEvalTypes),
  groupings: (analysis?.groupings?.length
    ? analysis.groupings
    : [emptyGroup()]
  ).map(cloneGroup),
});

// --- Saved -> UI shape ----------------------------------------------------
//
// A saved grouping carries the comparison type it was answered with and only
// that type's branch answers (see SCS_CLE_COMPARISON_TYPE_VALUES):
//   single_arm   qLabTreatmentGroups: ["TG1", …]
//   multi_arm    qLabTreatmentGroups: [{ name, role }, …]
//                qLabComparisonPairs: [{ treatment, comparator }, …]
//   dose_finding qLabDoseGroups: ["TG7", …]
// The single_arm names are saved under the same attribute as the multi_arm
// treatment groups but belong to the Non-Comparative question on screen, so
// they are put back on that one; Q4 goes back to its on-screen wording.
const restoreGroup = saved => {
  const comparisonType =
    SCS_CLE_COMPARISON_TYPE_LABELS[saved?.qLabComparisonType] || "";
  const treatmentGroups = Array.isArray(saved?.qLabTreatmentGroups)
    ? saved.qLabTreatmentGroups
    : [];
  // Both branches tolerate either spelling of an entry ("TG1" / { name: "TG1" }).
  const asName = tg => (typeof tg === "string" ? tg : tg?.name || "");
  return cloneGroup({
    ...saved,
    qLabComparisonType: comparisonType,
    qLabNonComparativeGroupNames:
      comparisonType === "Non-Comparative"
        ? treatmentGroups.map(asName)
        : undefined,
    qLabTreatmentGroups:
      comparisonType === "Comparative"
        ? treatmentGroups.map(tg => ({
            name: asName(tg),
            role: typeof tg === "string" ? "" : tg?.role,
          }))
        : undefined,
  });
};

const restoreAnalysis = analysis => ({
  ...emptyAnalysis(),
  qLabTable: analysis?.qLabTable || "",
  qLabEvalTypes: orderedEvalTypes(analysis?.qLabEvalTypes),
  groupings: (analysis?.groupings?.length
    ? analysis.groupings
    : [emptyGroup()]
  ).map(restoreGroup),
});

// The saved answers arrive either bare or wrapped, and as a list of sections or
// as a single section; collapse every shape to a plain list of sections.
const savedAnalysesFrom = payload => {
  const section = payload?.clinical_laboratory_evaluations || payload;
  if (!section || typeof section !== "object") return [];
  if (Array.isArray(section)) return section.filter(Boolean);
  const analyses = section.analyses;
  if (Array.isArray(analyses)) return analyses.filter(Boolean);
  if (analyses && typeof analyses === "object") return [analyses];
  return [];
};

const buildAnalyses = saved => {
  const blocks = (saved || []).map(restoreAnalysis);
  return blocks.length > 0 ? blocks : [emptyAnalysis()];
};

const toOptions = values => (values || []).map(v => ({ value: v, label: v }));
const fromOptions = options => (options || []).map(option => option.value);

// Render a single-select's chosen value as a chip/tag so it visually matches
// the multi-select dropdowns — look only, the control stays single-select
// (copied from the Analysis of Adverse Events page).
const SINGLE_VALUE_CHIP_STYLES = mergeSelectStyles(SELECT_STYLES, {
  singleValue: provided => ({
    ...provided,
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "calc(100% - 8px)",
    backgroundColor: "#eef1f5",
    borderRadius: "4px",
    padding: "3px 6px",
    margin: "2px",
    fontSize: "13px",
    boxSizing: "border-box",
  }),
});

const SCSClinicalLaboratoryEvaluations = () => {
  const { studyId: summaryId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // scs:wizards:read gates the GET that pre-populates this page; writing it
  // back needs scs:wizards:update on top, so without either the Save button is
  // disabled and Next navigates without submitting.
  const canReadWizards = hasPermission(PERMISSIONS.scsWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.scsWizardsUpdate);

  // The Q1 table dropdown is populated from the safety-summary TFL content in
  // the scsData store once the uploaded files have been PROCESSED; until then
  // fall back to the bundled placeholders.
  const { tableNames: scsTableNames } = useScsTableOptions();
  const tableOptions = scsTableNames.length
    ? scsTableNames
    : SCS_GROUPING_TABLE_OPTIONS;

  const [studyNameOptions, setStudyNameOptions] = useState([]);
  const [questionText, setQuestionText] = useState({});
  // One entry per table/analysis section — the only repeatable unit this page
  // is organised around.
  const [analyses, setAnalyses] = useState([emptyAnalysis()]);
  // Which table/analysis accordion items are open (by array index). The first
  // is expanded when the page loads; a newly added / duplicated section (always
  // the last) becomes the only expanded one.
  const [openAnalysisIndexes, setOpenAnalysisIndexes] = useState(
    () => new Set([0])
  );

  // Q2 is required per table/analysis section: { [analysisIndex]: message }.
  const [analysisErrors, setAnalysisErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  // Table/analysis blocks, so a newly added one — or the one holding the first
  // validation error — can be scrolled into view.
  const analysisRefs = useRef({});
  const pendingScrollRef = useRef(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Study name options come from the same endpoint the Description of Studies
  // page uses for its Q2A study dropdowns.
  useEffect(() => {
    CsrAPI.getAllStudiesForLaySummaries()
      .then(allStudies => {
        if (!mountedRef.current) return;
        const names = (allStudies || [])
          .map(study => study?.name)
          .filter(Boolean);
        setStudyNameOptions([...new Set(names)]);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Question wording is served from the shared safety-summary questions
  // endpoint; fall back to the bundled constants when it is unavailable.
  useEffect(() => {
    CsrAPI.getSafetySummaryQuestions()
      .then(data => {
        if (mountedRef.current && data) setQuestionText(data);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Pre-populate previously saved wizard state. Only the first section is left
  // expanded once the saved answers are restored.
  useEffect(() => {
    if (!summaryId) return;
    // Without scs:wizards:read the endpoint is never called; the page starts
    // from its empty form instead.
    if (!canReadWizards) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummaryClinicalLaboratoryEvaluations({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        setAnalyses(buildAnalyses(savedAnalysesFrom(data)));
        setOpenAnalysisIndexes(new Set([0]));
      })
      .catch(error => {
        if (!mountedRef.current) return;
        // A 404 simply means nothing has been saved yet — start with defaults.
        if (error?.response?.status === 404) return;
        if (isProduction) Sentry.captureException(error);
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to load previously saved data.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setAjaxLoading(false);
      });
  }, [summaryId, setAjaxLoading, canReadWizards]);

  // Scroll a newly added / duplicated table/analysis section — or the one
  // holding the first validation error — into view. Walk up to the named scroll
  // container so we never scroll the browser viewport (matches the Analysis of
  // Adverse Events block behaviour).
  useEffect(() => {
    const index = pendingScrollRef.current;
    if (index === null || index === undefined) return;
    pendingScrollRef.current = null;
    const el = analysisRefs.current[index];
    if (!el) return;
    let wrapper = el.parentElement;
    while (
      wrapper &&
      !wrapper.classList.contains("dashboard-content__wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper) {
      const elTop = el.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
    }
  });

  // --- Accordion open/closed state ----------------------------------------
  const toggleAnalysis = index =>
    setOpenAnalysisIndexes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  // --- Table/analysis and comparison group mutators -----------------------
  const updateAnalysis = (analysisIndex, updater) =>
    setAnalyses(prev =>
      prev.map((row, i) => (i === analysisIndex ? updater(row) : row))
    );

  const updateGroup = (analysisIndex, groupIndex, updater) =>
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      groupings: (analysis.groupings || []).map((row, i) =>
        i === groupIndex ? updater(row) : row
      ),
    }));

  // "Add another table/analysis" appends an empty section, "Duplicate another
  // table/analysis" appends a copy of the last one and the per-section
  // "Duplicate" pill appends a copy of that section. New sections always go to
  // the end and become the only expanded accordion item.
  const appendAnalysis = factory => {
    const added = factory(analyses);
    if (!added) return;
    setAnalyses(prev => [...prev, added]);
    pendingScrollRef.current = analyses.length;
    setOpenAnalysisIndexes(new Set([analyses.length]));
  };

  const addAnalysis = () => appendAnalysis(() => emptyAnalysis());
  const duplicateAnalysis = analysisIndex =>
    appendAnalysis(list =>
      list[analysisIndex] ? cloneAnalysis(list[analysisIndex]) : null
    );
  const duplicateAnalysisAbove = () =>
    appendAnalysis(list =>
      list.length ? cloneAnalysis(list[list.length - 1]) : null
    );
  const removeAnalysis = analysisIndex => {
    if (analyses.length <= MIN_BLOCK_ENTRIES) return;
    setAnalyses(prev => prev.filter((_, i) => i !== analysisIndex));
    // The remaining sections shift down a place, so drop the stale open indexes
    // and leave the last remaining section expanded.
    setOpenAnalysisIndexes(new Set([analyses.length - 2]));
    clearError(analysisIndex);
  };

  // "Add another group" / "Duplicate group above" repeat Q3–Q6 inside one
  // table/analysis section.
  const addGroup = analysisIndex =>
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      groupings: [...(analysis.groupings || []), emptyGroup()],
    }));
  const duplicateGroup = (analysisIndex, groupIndex) =>
    updateAnalysis(analysisIndex, analysis => {
      const groupings = analysis.groupings || [];
      if (!groupings[groupIndex]) return analysis;
      return {
        ...analysis,
        groupings: [...groupings, cloneGroup(groupings[groupIndex])],
      };
    });
  const duplicateGroupAbove = analysisIndex =>
    updateAnalysis(analysisIndex, analysis => {
      const groupings = analysis.groupings || [];
      if (!groupings.length) return analysis;
      return {
        ...analysis,
        groupings: [...groupings, cloneGroup(groupings[groupings.length - 1])],
      };
    });
  const removeGroup = (analysisIndex, groupIndex) =>
    updateAnalysis(analysisIndex, analysis => {
      const groupings = analysis.groupings || [];
      if (groupings.length <= MIN_BLOCK_ENTRIES) return analysis;
      return {
        ...analysis,
        groupings: groupings.filter((_, i) => i !== groupIndex),
      };
    });

  const clearError = analysisIndex =>
    setAnalysisErrors(prev => {
      if (prev[analysisIndex] === undefined) return prev;
      const next = { ...prev };
      delete next[analysisIndex];
      return next;
    });

  // Q1 — clearing the CLE table ends the section, so drop the Q2 error with it.
  const setLabTable = (analysisIndex, value) => {
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      qLabTable: value,
    }));
    if (!value) clearError(analysisIndex);
  };

  const setLabEvalTypes = (analysisIndex, types) => {
    const selected = orderedEvalTypes(types);
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      qLabEvalTypes: selected,
    }));
    if (selected.length) clearError(analysisIndex);
  };

  // Repeatable lists always keep at least one entry; the first is non-removable.
  const baseGroupNames = row =>
    (row.qLabNonComparativeGroupNames || []).length > 0
      ? row.qLabNonComparativeGroupNames
      : [""];
  const baseTreatmentGroups = row =>
    (row.qLabTreatmentGroups || []).length > 0
      ? row.qLabTreatmentGroups
      : [emptyTreatmentGroup()];
  const basePairs = row =>
    (row.qLabComparisonPairs || []).length > 0
      ? row.qLabComparisonPairs
      : [emptyComparisonPair()];
  const baseDoseGroups = row =>
    (row.qLabDoseGroups || []).length > 0 ? row.qLabDoseGroups : [""];

  // Q2 needs at least one CLE category once a table has been picked in Q1; a
  // section left without a table reports nothing and so needs no answers.
  const validate = () => {
    const errors = {};
    analyses.forEach((analysis, index) => {
      if (!(analysis.qLabTable || "").trim()) return;
      if ((analysis.qLabEvalTypes || []).length > 0) return;
      errors[index] = "Select at least one evaluation";
    });
    setAnalysisErrors(errors);

    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid !== undefined) {
      const index = Number(firstInvalid);
      // Expand the offending section before scrolling to it, otherwise the
      // question is hidden inside a collapsed accordion item.
      setOpenAnalysisIndexes(prev => new Set(prev).add(index));
      pendingScrollRef.current = index;
      return false;
    }
    return true;
  };

  // Only the answers belonging to the comparison type picked in Q4 are
  // persisted, under the attributes of the kind of grouping that type is saved
  // as (mirrors buildAdditionalGroupings on the Analysis of Adverse Events
  // page):
  //   single_arm   -> qLabTreatmentGroups (the plain group names)
  //   multi_arm    -> qLabTreatmentGroups ({ name, role }) and
  //                   qLabComparisonPairs
  //   dose_finding -> qLabDoseGroups
  // With Q4 unanswered only the common answers are saved, so nothing typed
  // into a branch is written under a comparison type the user did not pick.
  const buildGroupPayload = group => {
    const common = {
      qLabAdditionalGroupingName: group.qLabAdditionalGroupingName || "",
      qLabAdditionalGroupingStudies: group.qLabAdditionalGroupingStudies || [],
    };
    const comparisonType =
      SCS_CLE_COMPARISON_TYPE_VALUES[group.qLabComparisonType];
    if (comparisonType === "single_arm") {
      return {
        ...common,
        qLabComparisonType: comparisonType,
        // The Non-Comparative names have no role question on screen.
        qLabTreatmentGroups: baseGroupNames(group)
          .map(name => (name || "").trim())
          .filter(Boolean),
      };
    }
    if (comparisonType === "multi_arm") {
      return {
        ...common,
        qLabComparisonType: comparisonType,
        qLabTreatmentGroups: baseTreatmentGroups(group)
          .filter(tg => (tg?.name || "").trim())
          .map(tg => ({ name: tg.name, role: roleForPayload(tg.role) })),
        qLabComparisonPairs: basePairs(group).filter(
          pair => pair?.treatment || pair?.comparator
        ),
      };
    }
    if (comparisonType === "dose_finding") {
      return {
        ...common,
        qLabComparisonType: comparisonType,
        qLabDoseGroups: baseDoseGroups(group)
          .map(dose => (dose || "").trim())
          .filter(Boolean),
      };
    }
    return common;
  };

  const buildAnalysisPayload = analysis => {
    const table = (analysis.qLabTable || "").trim();
    // Q1 left blank ends the section, so nothing else is reported for it.
    if (!table) return { qLabTable: null };

    return {
      qLabTable: table,
      qLabEvalTypes: orderedEvalTypes(analysis.qLabEvalTypes),
      groupings: (analysis.groupings || []).map(buildGroupPayload),
    };
  };

  // Q4 is a button group with no way to un-pick the comparison type, so a
  // payload carrying nothing but comparison types is a page the user emptied:
  // it is saved as {} rather than as a section of blank answers.
  const buildPayload = () =>
    emptyWhenUnanswered({ analyses: analyses.map(buildAnalysisPayload) }, [
      "qLabComparisonType",
    ]);

  // Persists the wizard state via PATCH .../wizards/labs. Shared by the "Save"
  // button (stays on the page) and the "Safety in Special Groups" button
  // (navigates on success); returns whether the save succeeded so the caller
  // can decide what to do next.
  const saveWizardState = async () => {
    if (isSubmitting) return false;
    if (!canSaveWizards) return false;
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    setIsSubmitting(true);
    try {
      await CsrAPI.saveSafetySummaryClinicalLaboratoryEvaluations({
        summaryId,
        state: buildPayload(),
      });
      return true;
    } catch (error) {
      if (isProduction) Sentry.captureException(error);
      if (mountedRef.current) {
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to save. Please try again.");
        }
      }
      return false;
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!canSaveWizards) {
      navigate(`/scs/${summaryId}/safety-in-special-groups`);
      return;
    }

    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      navigate(`/scs/${summaryId}/safety-in-special-groups`);
    }
  };

  const handleSave = async event => {
    event?.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      toast.success("Data saved successfully!");
    }
  };

  const stringSelectOptions = values => [
    { value: "", label: "Select..." },
    ...values.map(v => ({ value: v, label: v })),
  ];

  const removeButton = (label, onClick) => (
    <CButton
      color={"danger"}
      shape={"pill"}
      aria-label={label}
      data-tooltip-id={REMOVE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <BinIcon />
    </CButton>
  );

  const addButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <PlusIcon />
    </CButton>
  );

  // Solid-blue pill sitting at the top of each block. A section holds both a
  // table/analysis and a group control, so each carries its own label.
  const duplicateBlockButton = (label, onClick) => (
    <CButton
      color={"primary"}
      shape={"pill"}
      className={"scs-dos__duplicate"}
      aria-label={label}
      data-tooltip-id={DUPLICATE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <CopyIcon /> Duplicate
    </CButton>
  );

  // Outlined pill pairing with the matching "Add another …" control.
  const duplicateAboveButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <CopyOutlineIcon />
    </CButton>
  );

  // Endpoint wording wins — under this page's attribute first, then under the
  // names the endpoint carries the same question by; fall back to the bundled
  // question text.
  const qText = key => {
    const question = SCS_CLE_QUESTIONS[key];
    const served = [key, ...(question?.textKeys || [])]
      .map(name => questionText[name])
      .find(Boolean);
    return served || question?.text || "";
  };

  // CSR-style numbered question: index badge in the left gutter, label +
  // control in the item column (mirrors QuestionItem).
  const renderQuestion = (key, control, numberOverride) => {
    const question = SCS_CLE_QUESTIONS[key];
    if (!question) return null;
    return (
      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>
            {numberOverride ?? question.number}
          </span>
        </div>
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              {qText(key)}
              {question.required && (
                <span className={"questions-content__required-asterisk"}>
                  {" "}
                  *
                </span>
              )}
            </span>
            {question.tooltip && (
              <QuestionItemInfo
                questionName={key}
                notes={{ [key]: question.tooltip }}
              />
            )}
          </div>
          <div className={"questions-content__input"}>{control}</div>
        </div>
      </div>
    );
  };

  // Q1 — single-select of the SCS TFL tables. Left blank, nothing below it is
  // shown.
  const renderLabTable = (analysis, a, number) =>
    renderQuestion(
      "qLabTable",
      <Select
        className={"scs-dos__control"}
        inputId={`lab-table-${a}`}
        styles={SINGLE_VALUE_CHIP_STYLES}
        options={toOptions(tableOptions)}
        value={
          analysis.qLabTable
            ? { value: analysis.qLabTable, label: analysis.qLabTable }
            : null
        }
        onChange={selected => setLabTable(a, selected ? selected.value : "")}
        placeholder={"Select laboratory evaluation table"}
        isClearable
      />,
      number
    );

  // Q2 — multi-select of the CLE categories. The categories themselves are the
  // whole answer; no per-category follow-up question is asked.
  const renderLabEvalTypes = (analysis, a, number) => {
    const error = analysisErrors[a];
    return renderQuestion(
      "qLabEvalTypes",
      <>
        <Select
          styles={SELECT_STYLES}
          isMulti
          className={"scs-dos__control"}
          inputId={`lab-eval-types-${a}`}
          options={toOptions(LAB_EVAL_TYPE_LABELS)}
          value={toOptions(orderedEvalTypes(analysis.qLabEvalTypes))}
          onChange={options => setLabEvalTypes(a, fromOptions(options))}
          placeholder={"Select evaluations"}
        />
        {error && <span className={"errmsg"}>{error}</span>}
      </>,
      number
    );
  };

  // Q3 — free-text grouping name plus the studies in that grouping (the same
  // pair of controls as Q3 of the Analysis of Adverse Events page).
  const renderAdditionalGrouping = (group, a, i, number) =>
    renderQuestion(
      "qLabAdditionalGroupingName",
      <div className={"questions-content__input-group"}>
        <div className={"scs-dos__stacked-fields"}>
          <div className={"form-component"}>
            <label htmlFor={`cle-grouping-name-${a}-${i}`}>Grouping Name</label>
            <CFormInput
              id={`cle-grouping-name-${a}-${i}`}
              value={group.qLabAdditionalGroupingName || ""}
              placeholder={"e.g. Non-Comp(Total)"}
              onChange={e =>
                updateGroup(a, i, row => ({
                  ...row,
                  qLabAdditionalGroupingName: e.target.value,
                }))
              }
            />
          </div>
          <div className={"form-component"}>
            <label>Studies</label>
            <Select
              styles={SELECT_STYLES}
              isMulti
              className={"scs-dos__control"}
              inputId={`cle-grouping-studies-${a}-${i}`}
              options={toOptions(studyNameOptions)}
              value={toOptions(group.qLabAdditionalGroupingStudies)}
              onChange={options =>
                updateGroup(a, i, row => ({
                  ...row,
                  qLabAdditionalGroupingStudies: fromOptions(options),
                }))
              }
              placeholder={"Select studies"}
            />
          </div>
        </div>
        <span className={"empty-space-layout-helper"}></span>
      </div>,
      number
    );

  // Q4 — comparison type, the same single-select button group as Q4 of the
  // Analysis of Adverse Events page.
  const renderComparisonType = (group, a, i, number) =>
    renderQuestion(
      "qLabComparisonType",
      SCS_COMPARISON_TYPE_OPTIONS.map(option => (
        <React.Fragment key={option}>
          <input
            type={"radio"}
            className={"btn-check"}
            name={`comparison-type-${a}-${i}`}
            id={`comparison-type-${a}-${i}-${option}`}
            autoComplete={"off"}
            checked={(group.qLabComparisonType || "") === option}
            onChange={() =>
              updateGroup(a, i, row => ({
                ...row,
                qLabComparisonType: option,
              }))
            }
          />
          <label
            className={"btn btn-light"}
            htmlFor={`comparison-type-${a}-${i}-${option}`}>
            {option}
          </label>
        </React.Fragment>
      )),
      number
    );

  const renderNonComparativeGroupNames = (group, a, i, number) =>
    renderQuestion(
      "qLabNonComparativeGroupNames",
      <>
        {baseGroupNames(group).map((groupName, gnIndex) => (
          <div
            key={`ncgn-${a}-${i}-${gnIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={groupName}
                placeholder={"Please enter treatment group name"}
                onChange={e =>
                  updateGroup(a, i, row => ({
                    ...row,
                    qLabNonComparativeGroupNames: baseGroupNames(row).map(
                      (name, index) =>
                        index === gnIndex ? e.target.value : name
                    ),
                  }))
                }
              />
            </div>
            {baseGroupNames(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove group", () =>
                updateGroup(a, i, row => ({
                  ...row,
                  qLabNonComparativeGroupNames: baseGroupNames(row).filter(
                    (_, index) => index !== gnIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add another", () =>
          updateGroup(a, i, row => ({
            ...row,
            qLabNonComparativeGroupNames: [...baseGroupNames(row), ""],
          }))
        )}
      </>,
      number
    );

  const renderTreatmentGroups = (group, a, i, number) =>
    renderQuestion(
      "qLabTreatmentGroups",
      <>
        {baseTreatmentGroups(group).map((tg, tgIndex) => (
          <div
            key={`tg-${a}-${i}-${tgIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Name</label>
              <CFormInput
                value={tg.name}
                placeholder={"Treatment group name"}
                onChange={e =>
                  updateGroup(a, i, row => ({
                    ...row,
                    qLabTreatmentGroups: baseTreatmentGroups(row).map(
                      (t, index) =>
                        index === tgIndex ? { ...t, name: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Role</label>
              <CFormSelect
                value={tg.role}
                options={stringSelectOptions(SCS_TREATMENT_GROUP_ROLE_OPTIONS)}
                onChange={e =>
                  updateGroup(a, i, row => ({
                    ...row,
                    qLabTreatmentGroups: baseTreatmentGroups(row).map(
                      (t, index) =>
                        index === tgIndex ? { ...t, role: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            {baseTreatmentGroups(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove treatment group", () =>
                updateGroup(a, i, row => ({
                  ...row,
                  qLabTreatmentGroups: baseTreatmentGroups(row).filter(
                    (_, index) => index !== tgIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Treatment Group", () =>
          updateGroup(a, i, row => ({
            ...row,
            qLabTreatmentGroups: [
              ...baseTreatmentGroups(row),
              emptyTreatmentGroup(),
            ],
          }))
        )}
      </>,
      number
    );

  const renderComparisonPairs = (group, a, i, number) => {
    // The Treatment / Comparator dropdowns list the names typed into Q5 for
    // this same group (mirrors the Analysis of Adverse Events Q5 -> Q6 flow).
    const groupNameOptions = stringSelectOptions([
      ...new Set(
        (group.qLabTreatmentGroups || [])
          .map(tg => (tg?.name || "").trim())
          .filter(Boolean)
      ),
    ]);
    return renderQuestion(
      "qLabComparisonPairs",
      <>
        {basePairs(group).map((pair, pairIndex) => (
          <div
            key={`pair-${a}-${i}-${pairIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Treatment</label>
              <CFormSelect
                value={pair.treatment}
                options={groupNameOptions}
                onChange={e =>
                  updateGroup(a, i, row => ({
                    ...row,
                    qLabComparisonPairs: basePairs(row).map((p, index) =>
                      index === pairIndex
                        ? { ...p, treatment: e.target.value }
                        : p
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Comparator</label>
              <CFormSelect
                value={pair.comparator}
                options={groupNameOptions}
                onChange={e =>
                  updateGroup(a, i, row => ({
                    ...row,
                    qLabComparisonPairs: basePairs(row).map((p, index) =>
                      index === pairIndex
                        ? { ...p, comparator: e.target.value }
                        : p
                    ),
                  }))
                }
              />
            </div>
            {pairIndex === 0 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove comparison pair", () =>
                updateGroup(a, i, row => ({
                  ...row,
                  qLabComparisonPairs: basePairs(row).filter(
                    (_, index) => index !== pairIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Comparison Pair", () =>
          updateGroup(a, i, row => ({
            ...row,
            qLabComparisonPairs: [...basePairs(row), emptyComparisonPair()],
          }))
        )}
      </>,
      number
    );
  };

  const renderDoseGroups = (group, a, i, number) =>
    renderQuestion(
      "qLabDoseGroups",
      <>
        {baseDoseGroups(group).map((dose, doseIndex) => (
          <div
            key={`dose-${a}-${i}-${doseIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={dose}
                placeholder={"Dose group label"}
                onChange={e =>
                  updateGroup(a, i, row => ({
                    ...row,
                    qLabDoseGroups: baseDoseGroups(row).map((d, index) =>
                      index === doseIndex ? e.target.value : d
                    ),
                  }))
                }
              />
            </div>
            {baseDoseGroups(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove dose group", () =>
                updateGroup(a, i, row => ({
                  ...row,
                  qLabDoseGroups: baseDoseGroups(row).filter(
                    (_, index) => index !== doseIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Dose Group", () =>
          updateGroup(a, i, row => ({
            ...row,
            qLabDoseGroups: [...baseDoseGroups(row), ""],
          }))
        )}
      </>,
      number
    );

  // One comparison group (Q3–Q6) inside a table/analysis section. Groups beyond
  // the first append "(groupNumber)" to every question index — the same
  // convention the Analysis of Adverse Events blocks use (e.g. 3(2), 4(2)).
  const renderGroupBlock = (group, a, i) => {
    const comparisonType = group.qLabComparisonType || "";
    const q = n => (i === 0 ? n : `${n}(${i + 1})`);
    // Branch questions are numbered sequentially from 5 in render order, based
    // on the single selected comparison type.
    let branchNumber = 5;
    const nonComparativeGroupNamesNumber =
      comparisonType === "Non-Comparative" ? q(branchNumber++) : null;
    const treatmentGroupsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const comparisonPairsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const doseGroupsNumber =
      comparisonType === "Dose-Finding" ? q(branchNumber++) : null;

    return (
      <div key={`group-${a}-${i}`} className={"scs-cle__group"}>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton("Duplicate this group", () =>
            duplicateGroup(a, i)
          )}
          {i > 0 && removeButton("Remove group", () => removeGroup(a, i))}
        </div>
        {renderAdditionalGrouping(group, a, i, q(3))}
        {renderComparisonType(group, a, i, q(4))}
        {comparisonType === "Non-Comparative" &&
          renderNonComparativeGroupNames(
            group,
            a,
            i,
            nonComparativeGroupNamesNumber
          )}
        {comparisonType === "Comparative" && (
          <>
            {renderTreatmentGroups(group, a, i, treatmentGroupsNumber)}
            {renderComparisonPairs(group, a, i, comparisonPairsNumber)}
          </>
        )}
        {comparisonType === "Dose-Finding" &&
          renderDoseGroups(group, a, i, doseGroupsNumber)}
      </div>
    );
  };

  // One table/analysis section: Q1, Q2 (+ 2A … 2E) and the comparison groups.
  // Leaving Q1 blank ends the section, so nothing below it is shown.
  const renderAnalysisBlock = (analysis, a) => {
    const hasTable = Boolean((analysis.qLabTable || "").trim());
    return (
      <>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton("Duplicate this table/analysis", () =>
            duplicateAnalysis(a)
          )}
          {analyses.length > MIN_BLOCK_ENTRIES &&
            removeButton("Remove table/analysis", () => removeAnalysis(a))}
        </div>
        {renderLabTable(analysis, a, 1)}
        {hasTable && (
          <>
            {renderLabEvalTypes(analysis, a, 2)}
            {(analysis.groupings || []).map((group, i) =>
              renderGroupBlock(group, a, i)
            )}
            <div className={"scs-dos__block-footer"}>
              {addButton("Add another group", () => addGroup(a))}
              {duplicateAboveButton("Duplicate group above", () =>
                duplicateGroupAbove(a)
              )}
            </div>
          </>
        )}
      </>
    );
  };

  // The table/analysis sections are what this page repeats: a single section is
  // rendered on its own, more than one is wrapped in an accordion (the first
  // one expanded on load).
  const renderAnalyses = () => (
    <>
      {analyses.length > 1 && (
        <div className={"scs-dos__accordion-controls"}>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() =>
              setOpenAnalysisIndexes(new Set(analyses.map((_, i) => i)))
            }>
            Expand all
          </CButton>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() => setOpenAnalysisIndexes(new Set())}>
            Collapse all
          </CButton>
        </div>
      )}
      {analyses.length === 1 ? (
        <div
          ref={el => (analysisRefs.current[0] = el)}
          className={"analysis-block"}>
          {renderAnalysisBlock(analyses[0], 0)}
        </div>
      ) : (
        <div className={"accordion scs-cle__analyses"}>
          {analyses.map((analysis, a) => {
            const isOpen = openAnalysisIndexes.has(a);
            return (
              <div
                key={`analysis-${a}`}
                className={"accordion-item"}
                ref={el => (analysisRefs.current[a] = el)}>
                <div className={"accordion-header"}>
                  <button
                    type={"button"}
                    className={`accordion-button${isOpen ? "" : " collapsed"}`}
                    onClick={() => toggleAnalysis(a)}>
                    {analysis.qLabTable || `Table/Analysis ${a + 1}`}
                  </button>
                </div>
                <CCollapse visible={isOpen} className={"accordion-collapse"}>
                  <div className={"accordion-body"}>
                    <div className={"analysis-block"}>
                      {renderAnalysisBlock(analysis, a)}
                    </div>
                  </div>
                </CCollapse>
              </div>
            );
          })}
        </div>
      )}
      <div className={"scs-dos__block-footer"}>
        {addButton("Add another table/analysis", addAnalysis)}
        {duplicateAboveButton(
          "Duplicate another table/analysis",
          duplicateAnalysisAbove
        )}
      </div>
    </>
  );

  // Reuses the `.scs-dos` styles — the block / accordion / duplicate controls
  // are identical to the Description of Studies wizard.
  const PageContent = () => (
    <div className={"questions-page scs-dos scs-cle"}>
      <div className={"questions-content"}>
        <div className={"questions-content__items"}>
          <ToastContainer />
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {renderAnalyses()}
          </form>
          <Tooltip
            id={REMOVE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
          <Tooltip
            id={DUPLICATE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
        </div>
      </div>
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Clinical Laboratory Evaluations"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Analysis of Adverse Events",
        buttonGoToAction: `/scs/${summaryId}/analysis-of-adverse-events`,
      }}
      saveButton={true}
      savePermission={[
        PERMISSIONS.scsWizardsRead,
        PERMISSIONS.scsWizardsUpdate,
      ]}
      saveButtonHandler={handleSave}
      saveButtonDisabled={isSubmitting}
      nextButton={{
        buttonTitle: isSubmitting ? "Saving..." : "Safety in Special Groups",
        buttonFormAction: FORM_ID,
        buttonGoToAction: `/scs/${summaryId}/safety-in-special-groups`,
        disabled: isSubmitting,
      }}
    />
  );
};

export default SCSClinicalLaboratoryEvaluations;
